import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { IoIosCloseCircleOutline } from 'react-icons/io';
import { FaTrash, FaEdit, FaPlus } from 'react-icons/fa';
import { MdBlock } from 'react-icons/md';
import { showToast } from '../../Components/utils/ShowToast';
import Loader from '../../Components/utils/Loader';
import Dropdown from '../../Components/utils/Dropdown';
import countries from '../../data/countries.json';

const API = process.env.REACT_APP_Backend_Host_Name;

function CustomTabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} id={`restriction-tabpanel-${index}`} aria-labelledby={`restriction-tab-${index}`} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return { id: `restriction-tab-${index}`, 'aria-controls': `restriction-tabpanel-${index}` };
}

const countryOptions = countries.map(c => ({ value: c.code, label: c.name }));

const AdminRestrictions = () => {
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Admin ${token}` };
    const [isLoading, setIsLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [restriction, setRestriction] = useState(null);

    const fetchRestrictions = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${API}/api/v1/restrictions`, { headers });
            if (res.data.success) setRestriction(res.data.restriction);
        } catch {
            showToast('Failed to load restrictions', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchRestrictions(); }, []);

    return (
        <div className="adminRestrictionsDiv">
            {isLoading && <Loader type="checkmate" />}

            <h1 className="primaryHeading">Restrictions</h1>
            <p className="subText">Manage payment country restrictions, seller account restrictions, and chat content warnings.</p>

            <div className="restrictionsContent">
                <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
                            <Tab sx={{ fontSize: '12px' }} label="Payment Restrictions" {...a11yProps(0)} />
                            <Tab sx={{ fontSize: '12px' }} label="Seller Restrictions" {...a11yProps(1)} />
                            <Tab sx={{ fontSize: '12px' }} label="Chat Warnings" {...a11yProps(2)} />
                        </Tabs>
                    </Box>
                    <CustomTabPanel value={tabValue} index={0}>
                        <PaymentRestrictions restriction={restriction} headers={headers} onUpdate={setRestriction} />
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={1}>
                        <SellerRestrictions restriction={restriction} headers={headers} onUpdate={setRestriction} />
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={2}>
                        <ChatWarnings restriction={restriction} headers={headers} onUpdate={setRestriction} />
                    </CustomTabPanel>
                </Box>
            </div>
        </div>
    );
};

export default AdminRestrictions;


function PaymentRestrictions({ restriction, headers, onUpdate }) {
    const [selectedMethod, setSelectedMethod] = useState('stripe');
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const methodLabels = { stripe: 'Stripe', wise: 'Wise', paypal: 'PayPal' };
    const restrictedList = restriction?.restrictedCountries?.[selectedMethod] || [];

    const filteredList = useMemo(() => {
        if (!search) return restrictedList;
        return restrictedList.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));
    }, [restrictedList, search]);

    const availableCountries = useMemo(() => {
        const restrictedCodes = new Set(restrictedList.map(c => c.code));
        return countryOptions.filter(c => !restrictedCodes.has(c.value));
    }, [restrictedList]);

    const addCountry = async () => {
        if (!selectedCountry) return;
        setLoading(true);
        try {
            const res = await axios.post(`${API}/api/v1/restrictions/country/add`, {
                method: selectedMethod,
                country: { name: selectedCountry.label, code: selectedCountry.value }
            }, { headers });
            if (res.data.success) {
                onUpdate(prev => ({ ...prev, restrictedCountries: res.data.restrictedCountries }));
                setSelectedCountry(null);
                showToast('Country added to restricted list', 'success');
            }
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to add country', 'error');
        } finally {
            setLoading(false);
        }
    };

    const removeCountry = async (countryCode) => {
        setLoading(true);
        try {
            const res = await axios.post(`${API}/api/v1/restrictions/country/remove`, {
                method: selectedMethod, countryCode
            }, { headers });
            if (res.data.success) {
                onUpdate(prev => ({ ...prev, restrictedCountries: res.data.restrictedCountries }));
                showToast('Country removed from restricted list', 'success');
            }
        } catch {
            showToast('Failed to remove country', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="restrictionSection">
            <div className="sectionHeader">
                <div>
                    <h2 className="secondaryHeading"><span>Payment</span> Country Restrictions</h2>
                    <p className="sectionDesc">Sellers from restricted countries cannot request withdrawals using that payment method.</p>
                </div>
            </div>

            <div className="methodTabs">
                {Object.keys(methodLabels).map(key => (
                    <button key={key} className={`methodTab ${selectedMethod === key ? 'active' : ''}`} onClick={() => { setSelectedMethod(key); setSearch(''); }}>
                        {methodLabels[key]}
                        <span className="badge">{restriction?.restrictedCountries?.[key]?.length || 0}</span>
                    </button>
                ))}
            </div>

            <div className="addCountryRow">
                <div className="dropdownWrapper">
                    <Dropdown
                        options={availableCountries}
                        value={selectedCountry}
                        onChange={setSelectedCountry}
                        placeholder="Select a country to restrict..."
                    />
                </div>
                <button className="primaryBtn" onClick={addCountry} disabled={!selectedCountry || loading}>
                    <FaPlus /> Add Country
                </button>
            </div>

            {restrictedList.length > 0 && (
                <input
                    type="text"
                    className="inputField searchInput"
                    placeholder={`Search restricted countries for ${methodLabels[selectedMethod]}...`}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            )}

            <div className="countriesList">
                {filteredList.length === 0 ? (
                    <div className="emptyState">
                        <MdBlock className="emptyIcon" />
                        <p>No countries restricted for {methodLabels[selectedMethod]}</p>
                    </div>
                ) : (
                    filteredList.map(country => (
                        <div key={country.code} className="countryChip">
                            <span className="countryFlag">{getFlagEmoji(country.code)}</span>
                            <span className="countryName">{country.name}</span>
                            <button className="chipRemoveBtn" onClick={() => removeCountry(country.code)} disabled={loading} title="Remove">
                                <IoIosCloseCircleOutline />
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}


function SellerRestrictions({ restriction, headers, onUpdate }) {
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const restrictedList = restriction?.restrictedSellerCountries || [];

    const filteredList = useMemo(() => {
        if (!search) return restrictedList;
        return restrictedList.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));
    }, [restrictedList, search]);

    const availableCountries = useMemo(() => {
        const restrictedCodes = new Set(restrictedList.map(c => c.code));
        return countryOptions.filter(c => !restrictedCodes.has(c.value));
    }, [restrictedList]);

    const addCountry = async () => {
        if (!selectedCountry) return;
        setLoading(true);
        try {
            const res = await axios.post(`${API}/api/v1/restrictions/seller-country/add`, {
                country: { name: selectedCountry.label, code: selectedCountry.value }
            }, { headers });
            if (res.data.success) {
                onUpdate(prev => ({ ...prev, restrictedSellerCountries: res.data.restrictedSellerCountries }));
                setSelectedCountry(null);
                showToast('Country added to seller restriction list', 'success');
            }
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to add country', 'error');
        } finally {
            setLoading(false);
        }
    };

    const removeCountry = async (countryCode) => {
        setLoading(true);
        try {
            const res = await axios.post(`${API}/api/v1/restrictions/seller-country/remove`, { countryCode }, { headers });
            if (res.data.success) {
                onUpdate(prev => ({ ...prev, restrictedSellerCountries: res.data.restrictedSellerCountries }));
                showToast('Country removed from seller restriction list', 'success');
            }
        } catch {
            showToast('Failed to remove country', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="restrictionSection">
            <div className="sectionHeader">
                <div>
                    <h2 className="secondaryHeading"><span>Seller</span> Account Restrictions</h2>
                    <p className="sectionDesc">Users from these countries cannot create a seller account on the platform.</p>
                </div>
            </div>

            <div className="addCountryRow">
                <div className="dropdownWrapper">
                    <Dropdown
                        options={availableCountries}
                        value={selectedCountry}
                        onChange={setSelectedCountry}
                        placeholder="Select a country to restrict..."
                    />
                </div>
                <button className="primaryBtn" onClick={addCountry} disabled={!selectedCountry || loading}>
                    <FaPlus /> Add Country
                </button>
            </div>

            {restrictedList.length > 0 && (
                <input
                    type="text"
                    className="inputField searchInput"
                    placeholder="Search restricted seller countries..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            )}

            <div className="countriesList">
                {filteredList.length === 0 ? (
                    <div className="emptyState">
                        <MdBlock className="emptyIcon" />
                        <p>No seller country restrictions added</p>
                    </div>
                ) : (
                    filteredList.map(country => (
                        <div key={country.code} className="countryChip">
                            <span className="countryFlag">{getFlagEmoji(country.code)}</span>
                            <span className="countryName">{country.name}</span>
                            <button className="chipRemoveBtn" onClick={() => removeCountry(country.code)} disabled={loading} title="Remove">
                                <IoIosCloseCircleOutline />
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}


function ChatWarnings({ restriction, headers, onUpdate }) {
    const [showModal, setShowModal] = useState(false);
    const [editingWarning, setEditingWarning] = useState(null);
    const [wordsInput, setWordsInput] = useState('');
    const [messageInput, setMessageInput] = useState('');
    const [loading, setLoading] = useState(false);

    const warnings = restriction?.chatWarnings || [];

    const openAddModal = () => {
        setEditingWarning(null);
        setWordsInput('');
        setMessageInput('');
        setShowModal(true);
    };

    const openEditModal = (warning) => {
        setEditingWarning(warning);
        setWordsInput(warning.words.join(', '));
        setMessageInput(warning.message);
        setShowModal(true);
    };

    const handleSave = async () => {
        const words = wordsInput.split(',').map(w => w.trim()).filter(Boolean);
        if (words.length === 0 || !messageInput.trim()) {
            showToast('Please provide both restricted words and a warning message', 'error');
            return;
        }

        setLoading(true);
        try {
            if (editingWarning) {
                const res = await axios.post(`${API}/api/v1/restrictions/chat-warning/update`, {
                    warningId: editingWarning._id, words, message: messageInput
                }, { headers });
                if (res.data.success) {
                    onUpdate(prev => ({ ...prev, chatWarnings: res.data.chatWarnings }));
                    showToast('Warning updated successfully', 'success');
                }
            } else {
                const res = await axios.post(`${API}/api/v1/restrictions/chat-warning/add`, {
                    words, message: messageInput
                }, { headers });
                if (res.data.success) {
                    onUpdate(prev => ({ ...prev, chatWarnings: res.data.chatWarnings }));
                    showToast('Warning added successfully', 'success');
                }
            }
            setShowModal(false);
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to save warning', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (warningId) => {
        if (!window.confirm('Are you sure you want to delete this warning?')) return;
        setLoading(true);
        try {
            const res = await axios.post(`${API}/api/v1/restrictions/chat-warning/delete`, { warningId }, { headers });
            if (res.data.success) {
                onUpdate(prev => ({ ...prev, chatWarnings: res.data.chatWarnings }));
                showToast('Warning deleted successfully', 'success');
            }
        } catch {
            showToast('Failed to delete warning', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="restrictionSection">
            <div className="sectionHeader">
                <div>
                    <h2 className="secondaryHeading"><span>Chat</span> Warning Rules</h2>
                    <p className="sectionDesc">Define restricted words/sentences. Users will see a warning when they try to use these in chat.</p>
                </div>
                <button className="primaryBtn" onClick={openAddModal}><FaPlus /> Add Rule</button>
            </div>

            <div className="warningsList">
                {warnings.length === 0 ? (
                    <div className="emptyState">
                        <MdBlock className="emptyIcon" />
                        <p>No chat warning rules defined</p>
                    </div>
                ) : (
                    warnings.map(warning => (
                        <div key={warning._id} className="warningCard">
                            <div className="warningCardContent">
                                <div className="warningWords">
                                    <span className="warningLabel">Restricted Words:</span>
                                    <div className="wordChips">
                                        {warning.words.map((word, i) => (
                                            <span key={i} className="wordChip">{word}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="warningMessage">
                                    <span className="warningLabel">Warning Message:</span>
                                    <p>{warning.message}</p>
                                </div>
                            </div>
                            <div className="warningActions">
                                <button className="iconBtn editBtn" onClick={() => openEditModal(warning)} title="Edit"><FaEdit /></button>
                                <button className="iconBtn deleteBtn" onClick={() => handleDelete(warning._id)} title="Delete"><FaTrash /></button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {showModal && (
                <div className="popupDiv">
                    <div className="popupContent">
                        <div className="form">
                            <h2 className="secondaryHeading">{editingWarning ? 'Edit' : 'Add'} <span>Chat Warning</span> Rule</h2>
                            <div className="horizontalLine"></div>
                            <div className="inputDiv">
                                <label>Restricted Words/Sentences <span>*</span></label>
                                <input
                                    type="text"
                                    className="inputField"
                                    value={wordsInput}
                                    onChange={e => setWordsInput(e.target.value)}
                                    placeholder="Enter words separated by commas (e.g., paypal, phone number, whatsapp)"
                                />
                                <small className="inputHint">Separate multiple words or phrases with commas</small>
                            </div>
                            <div className="inputDiv">
                                <label>Warning Message <span>*</span></label>
                                <textarea
                                    className="inputField"
                                    value={messageInput}
                                    onChange={e => setMessageInput(e.target.value)}
                                    placeholder="Enter the warning message users will see..."
                                    rows={3}
                                />
                            </div>
                        </div>
                        <div className="buttonsDiv" style={{ marginTop: '20px' }}>
                            <button className="secondaryBtn" onClick={() => setShowModal(false)}>Cancel</button>
                            <button className="primaryBtn" onClick={handleSave} disabled={loading}>
                                {loading ? 'Saving...' : (editingWarning ? 'Update Rule' : 'Add Rule')}
                            </button>
                        </div>
                    </div>
                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowModal(false)} />
                    </div>
                </div>
            )}
        </div>
    );
}


function getFlagEmoji(countryCode) {
    if (!countryCode || countryCode.length !== 2) return '';
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
}
